import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Plus, Edit2, Trash2, Power, X } from 'lucide-react';

export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    provider_code: '',
    category: 'Gaming',
    image: '',
    download_url: '',
    enabled: true,
    hot: false,
    provider_status: 'active',
    // Phase 2 currency overhaul — per-game bonus splits.
    first_time_bonus: 0,
    regular_bonus: 0,
    fp_first_time_bonus: 0,
    fp_regular_bonus: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gamesRes, providersRes] = await Promise.all([
        api.get('/api/admin/games'),
        api.get('/api/admin/providers')
      ]);
      setGames(gamesRes.data.data?.items || gamesRes.data.items || gamesRes.data.games || []);
      setProviders(providersRes.data.data?.items || providersRes.data.items || providersRes.data.providers || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/api/admin/games/${editing.id}`, formData);
        toast.success('Game updated');
      } else {
        await api.post('/api/admin/games', formData);
        toast.success('Game created');
      }
      setShowForm(false);
      setEditing(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this game?')) return;
    try {
      await api.delete(`/api/admin/games/${id}`);
      toast.success('Game deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggle = async (game) => {
    try {
      await api.patch(`/api/admin/games/${game.id}`, { enabled: !game.enabled });
      toast.success(game.enabled ? 'Game disabled' : 'Game enabled');
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      provider_code: '',
      category: 'Gaming',
      image: '',
      download_url: '',
      enabled: true,
      hot: false,
      provider_status: 'active',
      first_time_bonus: 0,
      regular_bonus: 0,
      fp_first_time_bonus: 0,
      fp_regular_bonus: 0,
    });
  };

  const openEdit = (game) => {
    setEditing(game);
    setFormData({
      name: game.name,
      provider_code: game.provider_code,
      category: game.category || 'Gaming',
      image: game.image || '',
      download_url: game.download_url || '',
      enabled: game.enabled,
      hot: game.hot || false,
      provider_status: game.provider_status || 'active',
      first_time_bonus: game.first_time_bonus ?? game.rules?.first_time_bonus ?? 0,
      regular_bonus: game.regular_bonus ?? game.rules?.regular_bonus ?? 0,
      fp_first_time_bonus: game.fp_first_time_bonus ?? game.rules?.fp_first_time_bonus ?? (game.first_time_bonus ?? 0),
      fp_regular_bonus: game.fp_regular_bonus ?? game.rules?.fp_regular_bonus ?? (game.regular_bonus ?? 0),
    });
    setShowForm(true);
  };

  const filtered = games.filter(g =>
    g.name?.toLowerCase().includes(search.toLowerCase()) ||
    g.provider_code?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="games-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Games ({games.length})</h2>
        <Button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Game
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search games..." className="pl-10 bg-[#131822] border-white/10" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filtered.map((game) => (
          <div key={game.id} className={`card p-3 ${!game.enabled ? 'opacity-60' : ''}`}>
            <div className="relative aspect-square rounded-lg overflow-hidden mb-3">
              <img src={game.image} alt={game.name} className="w-full h-full object-cover" />
              {game.hot && <span className="absolute top-2 right-2 badge badge-danger text-xs">HOT</span>}
              {!game.enabled && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-xs">Disabled</div>}
            </div>
            <h3 className="font-medium text-sm truncate">{game.name}</h3>
            <p className="text-xs text-gray-400">{game.provider_code}</p>
            {game.provider_status && game.provider_status !== 'active' && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold mt-1 inline-block ${game.provider_status === 'manual_only' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                {game.provider_status === 'manual_only' ? 'Manual Only' : 'Coming Soon'}
              </span>
            )}
            <div className="flex items-center gap-1 mt-2">
              <button onClick={() => openEdit(game)} className="p-1.5 hover:bg-white/5 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => handleToggle(game)} className={`p-1.5 hover:bg-white/5 rounded ${game.enabled ? 'text-green-400' : 'text-gray-400'}`}>
                <Power className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(game.id)} className="p-1.5 hover:bg-white/5 rounded text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131822] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit Game' : 'Add Game'}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-white/5 rounded"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Name</label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Provider</label>
                <select
                  value={formData.provider_code}
                  onChange={(e) => setFormData(p => ({ ...p, provider_code: e.target.value }))}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select provider</option>
                  {providers.map(p => (
                    <option key={p.id} value={p.code}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Category</label>
                <Input value={formData.category} onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Image URL</label>
                <Input value={formData.image} onChange={(e) => setFormData(p => ({ ...p, image: e.target.value }))} placeholder="https://..." className="bg-[#0B0E14] border-white/10" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Download/Play URL</label>
                <Input value={formData.download_url} onChange={(e) => setFormData(p => ({ ...p, download_url: e.target.value }))} placeholder="https://..." className="bg-[#0B0E14] border-white/10" />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData(p => ({ ...p, enabled: e.target.checked }))} className="w-4 h-4" />
                  <span className="text-sm">Enabled</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.hot} onChange={(e) => setFormData(p => ({ ...p, hot: e.target.checked }))} className="w-4 h-4" />
                  <span className="text-sm">Hot</span>
                </label>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Provider Status</label>
                <select value={formData.provider_status} onChange={(e) => setFormData(p => ({ ...p, provider_status: e.target.value }))}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="active">Active (Auto Create)</option>
                  <option value="manual_only">Manual Account Only</option>
                  <option value="coming_soon">Coming Soon</option>
                </select>
              </div>

              {/* Per-game legacy load fields are kept for compatibility, but
                  all player-facing rewards/bonuses are FP-only. */}
              <div className="rounded-xl p-3 space-y-3" style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.2)' }} data-testid="game-cash-bonus-section">
                <p className="text-xs font-semibold text-[#F5D76E] uppercase tracking-wide">Legacy Wallet Load Bonus % (keep 0)</p>
                <p className="text-[10px] text-gray-500">Do not use this for player rewards. Bonuses and rewards must be configured as FP credits only.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-400 block">Legacy First-Time %</label>
                    <Input type="number" min={0} max={1000} value={formData.first_time_bonus}
                      onChange={(e) => setFormData(p => ({ ...p, first_time_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[#0B0E14] border-white/10" data-testid="game-first-time-bonus" />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-400 block">Legacy Regular %</label>
                    <Input type="number" min={0} max={1000} value={formData.regular_bonus}
                      onChange={(e) => setFormData(p => ({ ...p, regular_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[#0B0E14] border-white/10" data-testid="game-regular-bonus" />
                  </div>
                </div>
              </div>
              <div className="rounded-xl p-3 space-y-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.3)' }} data-testid="game-fp-bonus-section">
                <p className="text-xs font-semibold text-indigo-300 uppercase tracking-wide">Free Play (FP) Bonuses</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-400 block">FP First-Time Bonus %</label>
                    <Input type="number" min={0} max={1000} value={formData.fp_first_time_bonus}
                      onChange={(e) => setFormData(p => ({ ...p, fp_first_time_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[#0B0E14] border-white/10" data-testid="game-fp-first-time-bonus" />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-400 block">FP Regular Bonus %</label>
                    <Input type="number" min={0} max={1000} value={formData.fp_regular_bonus}
                      onChange={(e) => setFormData(p => ({ ...p, fp_regular_bonus: parseFloat(e.target.value) || 0 }))}
                      className="bg-[#0B0E14] border-white/10" data-testid="game-fp-regular-bonus" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-500">Applied when the player funds the load with FP Credits instead of $.</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
                <Button type="submit" className="btn-primary">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
